import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusCircleIcon,
    MagnifyingGlassIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    BarsArrowDownIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    PencilSquareIcon,
    TrashIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const mockGoals = [
    { id: 1, name: 'Q3 Sales Target', assignedTo: 'John Smith', deadline: '2024-09-30', progress: 85, status: 'On Track', type: 'Sales' },
    { id: 2, name: 'Launch Mobile App', assignedTo: 'Jane Doe', deadline: '2024-12-15', progress: 40, status: 'At Risk', type: 'Product' },
    { id: 3, name: 'Complete HR Audit', assignedTo: 'Emily White', deadline: '2024-08-31', progress: 100, status: 'Completed', type: 'HR' },
    { id: 4, name: 'Marketing Campaign', assignedTo: 'David Lee', deadline: '2024-10-20', progress: 60, status: 'On Track', type: 'Marketing' },
    { id: 5, name: 'Refactor Legacy Code', assignedTo: 'Sarah Chen', deadline: '2024-11-01', progress: 95, status: 'Completed', type: 'Technology' },
];

const GoalsObjectives = () => {
    const [goals, setGoals] = useState(mockGoals);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'deadline', direction: 'ascending' });

    const filteredGoals = useMemo(() => {
        return goals.filter(goal =>
            goal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            goal.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            goal.status.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [goals, searchTerm]);

    const sortedGoals = useMemo(() => {
        let sortableGoals = [...filteredGoals];
        if (sortConfig.key) {
            sortableGoals.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableGoals;
    }, [filteredGoals, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <BarsArrowDownIcon className="w-4 h-4 ml-1 text-gray-400" />;
        return sortConfig.direction === 'ascending' ? <ArrowUpIcon className="w-4 h-4 ml-1" /> : <ArrowDownIcon className="w-4 h-4 ml-1" />;
    };

    const statusCounts = useMemo(() => {
        const counts = { 'On Track': 0, 'At Risk': 0, 'Completed': 0 };
        goals.forEach(goal => counts[goal.status]++);
        return counts;
    }, [goals]);

    const chartData = {
        labels: ['On Track', 'At Risk', 'Completed'],
        datasets: [{
            data: [statusCounts['On Track'], statusCounts['At Risk'], statusCounts['Completed']],
            backgroundColor: ['#2563EB', '#F59E0B', '#10B981'],
            borderColor: '#ffffff',
            borderWidth: 2,
        }],
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-100 to-pink-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Controls */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-purple-300">
                    <h1 className="text-4xl font-extrabold text-purple-800 tracking-tight mb-4 md:mb-0">
                        Goals & Objectives
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search goals..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 shadow-sm"
                            />
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            <span className="font-semibold">Add New Goal</span>
                        </motion.button>
                    </div>
                </div>

                {/* Summary & Charts */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Total Goals</h3>
                        <p className="text-4xl font-extrabold text-indigo-600 mt-2">{goals.length}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">On Track</h3>
                        <p className="text-4xl font-extrabold text-blue-600 mt-2">{statusCounts['On Track']}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">At Risk</h3>
                        <p className="text-4xl font-extrabold text-orange-600 mt-2">{statusCounts['At Risk']}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6 flex flex-col items-center justify-center"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                    >
                        <div className="w-full h-full max-h-40">
                            <Pie
                                data={chartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false } }
                                }}
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Goal Progress Table */}
                <motion.div
                    className="mt-8 bg-white rounded-3xl shadow-xl overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['name', 'assignedTo', 'deadline', 'progress', 'status', 'actions'].map(key => (
                                        <th
                                            key={key}
                                            onClick={() => key !== 'actions' && requestSort(key)}
                                            className={`px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider ${key !== 'actions' ? 'cursor-pointer hover:bg-gray-100 transition-colors' : 'text-right'}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                {key.replace(/([A-Z])/g, ' $1').charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').slice(1)}
                                                {key !== 'actions' && getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence initial={false}>
                                    {sortedGoals.length > 0 ? (
                                        sortedGoals.map(goal => (
                                            <motion.tr
                                                key={goal.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -20, transition: { duration: 0.3 } }}
                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{goal.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{goal.assignedTo}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{goal.deadline}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="w-24 bg-gray-200 rounded-full h-2.5">
                                                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${goal.progress}%` }}></div>
                                                    </div>
                                                    <span className="ml-2 text-xs font-semibold">{goal.progress}%</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        goal.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                        goal.status === 'On Track' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {goal.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-indigo-600 hover:text-indigo-900 transition-colors">
                                                            <EyeIcon className="h-5 w-5" />
                                                        </motion.button>
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-purple-600 hover:text-purple-900 transition-colors">
                                                            <PencilSquareIcon className="h-5 w-5" />
                                                        </motion.button>
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-red-600 hover:text-red-900 transition-colors">
                                                            <TrashIcon className="h-5 w-5" />
                                                        </motion.button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No goals found.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default GoalsObjectives;